import axios from 'axios';
import { LocalStorage } from 'quasar';
import logoutUtil from 'src/utils/cache';
import type { dynamicObj } from 'src/pages/assets';
import { LONG_REQUEST_THRESHOLD } from 'src/constants';
import { ApiConfiguration, RequestConfig } from 'src/types';
import { ApiError, RequestCancelError } from 'src/errors/index';
import { createCancelTokenHandler } from './cancelRequestHandler';
import { installTransientRetry } from './transientRetry';
import { attachTraceHeaders } from 'src/utils/searchTrace';
import type { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

const cancelTokenHandlerObject = createCancelTokenHandler('doc2app');
export abstract class ApiClient {
  protected readonly client: AxiosInstance;

  protected constructor(apiConfiguration: ApiConfiguration) {
    this.client = axios.create({
      // Default request timeout. Without this Axios falls back to 0 (unlimited),
      // which means a hung upstream (ingress pause, linkerd mesh hiccup) leaves
      // every idempotent fetch waiting forever. The boot-critical
      // /preferences/get_preferences call blocking the app-loading spinner for
      // 30+ seconds observed on 2026-04-21 was a direct consequence. A hit on
      // this timeout raises ECONNABORTED, which `installTransientRetry` already
      // treats as retryable for GET/HEAD/OPTIONS, so the UX recovers within the
      // normal backoff window instead of hanging. Long-running calls (large
      // uploads, exports) pass their own timeout via per-request config and
      // override this default.
      // Changed from 60_000 (1 minute) to 120_000 (2 minutes) specifically for 
      // timeout issue reported in OBO-35 for AI tags where request to AI takes 
      // longer than 1 minute. Averge response time for AI tags is around 1.8 minutes,
      // so setting it to 2 minutes to avoid timeout in such cases.
      timeout: 120_000,
      ...apiConfiguration,
    });
    this.client.defaults.headers.common['Accept'] =
      'application/json;charset=UTF-8';
    this.client.defaults.headers.common['Content-Type'] =
      'application/json;charset=UTF-8';
    //   this.client.defaults.headers.common['Authorization'] = token;

    // Transient-retry MUST be installed before any other response
    // interceptor that would observe the error (event dispatch,
    // logging, banner signal). When retry succeeds on attempt 2,
    // we want zero side-effects from attempt 1 — no spurious
    // `app:backend-unavailable` event, no warning log.
    installTransientRetry(this.client);

    // Attach request interceptor to record start time
    this.client.interceptors.request.use((config) => {
      config.metadata = { startTime: new Date() };
      attachTraceHeaders(config);
      return config;
    });

    // Attach response interceptor to log slow requests and user activity
    this.client.interceptors.response.use(
      (response) => {
        this.logResponseTime(response);
        this.logUserActivity(response);
        return response;
      },
      (error) => {
        this.logResponseTime(error);
        return Promise.reject(error);
      },
    );

    // Register debug collector interceptor for API call capture (Ctrl+Shift+P debug snapshot)
    import('src/boot/debugCollector').then(({ registerDebugInterceptor }) => {
      registerDebugInterceptor(this.client);
    }).catch(() => { /* debugCollector not yet available during early boot */ });
  }

  private logResponseTime(response: AxiosResponse | AxiosError) {
    const config = response.config;
    if (!config || !config.metadata || !config.metadata.startTime) {
      return;
    }

    const duration = new Date().getTime() - config.metadata.startTime.getTime();
    if (duration > LONG_REQUEST_THRESHOLD) {
      const url = config.url || 'UNKNOWN_URL';
      const method = config.method?.toUpperCase() || 'UNKNOWN_METHOD';
      const status = response.status ?? 'NO_STATUS';

      logInfo(
        '=====================================================================================================================',
      );
      logError(
        `Long-running ${method} request to ${url} took ${(duration / 1000).toFixed(2)} seconds. Status: ${status}`,
      );
      logInfo(
        '=====================================================================================================================',
      );
    }
  }

  private logUserActivity(response: AxiosResponse) {
    try {
      const config = response.config;
      if (!config) return;

      const url = config.url || 'UNKNOWN_URL';
      const method = config.method?.toUpperCase() || 'UNKNOWN_METHOD';
      const status = response.status;

      // Log successful API requests for user activity tracking
      if (status >= 200 && status < 300) {
        // Only log meaningful endpoints, skip health checks and frequent polling
        const skipPaths = ['/health', '/ping', '/status', '/websocket'];
        const shouldLog = !skipPaths.some((path) => url.includes(path));

        if (shouldLog && typeof globalThis.logInfo === 'function') {
          globalThis.logInfo(`User API activity: ${method} ${url}`, {
            method,
            url,
            status,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            referrer: document.referrer || 'direct',
          });
        }
      }
    } catch (error) {
      // Silently ignore logging errors to prevent breaking API calls
      console.warn('Failed to log user activity:', error);
    }
  }

  async post<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    options?: RequestConfig,
  ): Promise<TResponse> {
    try {
      const response = options
        ? await this.client.post<TResponse>(path, payload, options)
        : await this.client.post<TResponse>(path, payload);
      return this.handleResponse(response);
    } catch (error: any) {
      if (options && options.hasOwnProperty('error')) {
        const error_callback = options.error;
        if (error_callback) error_callback.call(this, error);
        return {} as TResponse;
      }
      await this.handleError(error);
    }
    return {} as TResponse;
  }

  async postWithCancel<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    options?: RequestConfig,
  ): Promise<TResponse> {
    try {
      options!.cancelToken =
        cancelTokenHandlerObject[path].handleRequestCancellation().token;

      const response = options
        ? await this.client.post<TResponse>(path, payload, options)
        : await this.client.post<TResponse>(path, payload);
      return this.handleResponse(response);
    } catch (error: any) {
      if (options && options.hasOwnProperty('error')) {
        const error_callback = options.error;
        if (error_callback) error_callback.call(this, error);
        return {} as TResponse;
      }
      await this.handleError(error);
    }
    return {} as TResponse;
  }

  async put<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    options?: RequestConfig,
  ): Promise<TResponse> {
    try {
      const response = options
        ? await this.client.put<TResponse>(path, payload, options)
        : await this.client.put<TResponse>(path, payload);
      return this.handleResponse(response);
    } catch (error: any) {
      if (options && options.hasOwnProperty('error')) {
        const error_callback = options.error;
        if (error_callback) error_callback.call(this, error);
        return {} as TResponse;
      }
      await this.handleError(error);
    }
    return {} as TResponse;
  }

  async get<TRequest, TResponse>(
    path: string,
    options?: RequestConfig,
  ): Promise<TResponse> {
    try {
      const response = await this.client.get<TResponse>(path, options);
      return this.handleResponse(response);
    } catch (error: any) {
      if (options && options.hasOwnProperty('error')) {
        const error_callback = options.error;
        if (error_callback) error_callback.call(this, error);
        return {} as TResponse;
      }
      await this.handleError(error);
    }
    return {} as TResponse;
  }

  async patch<TResponse>(
    path: string,
    payload: FormData | dynamicObj,
    options?: RequestConfig,
  ): Promise<TResponse> {
    try {
      const response = options
        ? await this.client.patch<TResponse>(path, payload, options)
        : await this.client.patch<TResponse>(path, payload);
      return this.handleResponse(response);
    } catch (error: any) {
      if (options && options.hasOwnProperty('error')) {
        const error_callback = options.error;
        if (error_callback) error_callback.call(this, error);
        return {} as TResponse;
      }
      await this.handleError(error);
    }
    return {} as TResponse;
  }

  async delete<TRequest, TResponse>(
    path: string,
    config?: RequestConfig,
  ): Promise<TResponse> {
    try {
      const response = config
        ? await this.client.delete<TResponse>(path, config)
        : await this.client.delete<TResponse>(path);
      return this.handleResponse(response);
    } catch (error) {
      await this.handleError(error);
    }
    return {} as TResponse;
  }

  protected createParams(record: Record<string, any>): URLSearchParams {
    const params: URLSearchParams = new URLSearchParams();
    for (const key in record) {
      if (Object.prototype.hasOwnProperty.call(record, key)) {
        const value: any = record[key];
        if (value !== null && value !== undefined) {
          params.append(key, value);
        } else {
          logDebug(
            `Param key '${key}' was null or undefined and will be ignored`,
          );
        }
      }
    }
    return params;
  }

  protected handleResponse<T>(response: AxiosResponse<T>): T {
    // Signal recovery: any 2xx response means the backend is reachable
    // again. The network-unavailable banner listens for this and hides
    // itself. Keeps the UX tight — user sees the transient error
    // banner flash for 2–3 seconds during a rolling deploy, then it
    // disappears on the next successful call.
    if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:backend-available'));
    }
    return response.data;
  }

  protected isAxiosError(value: any): value is AxiosError {
    return typeof value?.response === 'object';
  }

  protected async handleError(error: any): Promise<any> {
    if (error instanceof Error) {
      if (this.isAxiosError(error)) {
        if (error.response) {
          if (error.response.status && error.response.status === 503) {
            // Previously: every 503 redirected to /network_error, which
            // kicked the user out of the app entirely on a single
            // transient backend blip. In a distributed system (nginx
            // ingress → k8s service → FastAPI pod → Celery/Redis/OS),
            // 503 is common during rolling updates, pod restarts, or
            // temporary broker hiccups. A full-page redirect is the
            // wrong UX for something that self-heals in seconds.
            //
            // A 503 also does NOT raise the backend-unavailable banner:
            // the backend answered, it is just unhealthy/overloaded —
            // that is not a connectivity outage. The banner is reserved
            // for genuine network errors (see the Network Error branch
            // below). 503 is logged and thrown so the caller can handle
            // it inline (retry button, inline "try again").
            this.logErrorMessage(error.response);

            const data = error.response.data;
            if (typeof data === 'string' || !data) {
              throw new ApiError(error as any);
            }
            throw data;
          }

          // Unauthorized response status code
          if (error.response.status && error.response.status === 401) {
            // If the token is invalid or revoked, always refresh — even on
            // graceful pages.  A revoked token will never succeed, so
            // silently rethrowing just leaves the user stuck.
            const msg = typeof error.response.data === 'object'
              ? ((error.response.data as any)?.message || '')
              : String(error.response.data || '');
            const isTokenGone =
              /invalid token|token has been revoked/i.test(msg);

            if (isTokenGone) {
              console.warn('[API] Token invalid/revoked — forcing refresh');
              await this.tryToRefreshToken();
              return;
            }

            // On dashboard/settings pages, throw non-token 401s so the
            // component can display them (e.g. missing permissions).
            const isGracefulErrorPage =
              typeof window !== 'undefined' &&
              (window.location.pathname.includes('/prepare-dashboard') ||
               window.location.pathname.includes('/dashboard') ||
               window.location.pathname.includes('/settings/'));

            if (isGracefulErrorPage) {
              console.warn('[API] 401 error on graceful page - throwing to component for error handling');
              const data = error.response.data;
              if (typeof data === 'string' || !data) {
                throw new ApiError(error as any);
              }
              throw data;
            }

            // On other pages, attempt token refresh with redirect
            await this.tryToRefreshToken();
            return;
          }

          if (error.response && error.response.data) {
            const data = error.response.data;
            if (typeof data === 'string') {
              throw new ApiError(error as any);
            }
            throw data;
          }

          throw new ApiError(error as any);
        } else if (error.request) {
          // The request was made but no response was received
          // `error.request` is an instance of XMLHttpRequest in the browser
          throw new ApiError(error as any);
        }
      } else {
        // Something happened in setting up the request that triggered an Error
        if (error.message == 'Network Error') {
          // Same reasoning as the 503 branch above: a full-page
          // redirect on a transient network blip is too aggressive.
          // Log, emit event for the banner, then throw. The banner
          // stays visible until a subsequent successful call
          // dispatches `app:backend-available`, so the user gets
          // visual feedback without losing their place.
          this.logErrorMessage(error);
          if (typeof window !== 'undefined' && typeof CustomEvent !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('app:backend-unavailable', {
                detail: {
                  url: (error as any).config?.url,
                  reason: 'network-error',
                  timestamp: Date.now(),
                },
              }),
            );
          }
        }

        if (this.isAxiosCancel(error)) {
          throw new RequestCancelError(error);
        }

        throw new ApiError(error as any);
      }
    }

    throw new ApiError(error);
  }

  isAxiosCancel(error: any): boolean {
    if (
      error.hasOwnProperty('message') &&
      error.message.toLowerCase().includes('handlerequestcancellation')
    ) {
      logInfo('lets handle cancel event');
      return true;
    }
    return false;
  }

  protected logErrorMessage(error: any) {
    const errorJson = JSON.stringify(error);
    LocalStorage.set('network_error_log', errorJson);
  }

  private async tryToRefreshToken() {
    try {
      const { useAuthTokenStore } = await import('src/store/main');
      const auth_token_store = useAuthTokenStore();
      if (auth_token_store && typeof auth_token_store.tryToRefreshToken === 'function') {
        auth_token_store.tryToRefreshToken(true);
      }
    } catch {
      // Store not available (e.g., during tests or before Pinia initialization)
    }
  }
}
